<template>
  <ListPageShell>
    <template #header>
      <div class="rounded-xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6">
        <ReportHeader
          :report="report"
          :report-date="reportDate"
          :active-phase="activePhase"
          :save-status="activeDraftStatus"
          @update:report-date="changeReportDate"
        />
      </div>
    </template>

    <section v-if="loading" class="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white px-6 py-16 shadow-sm">
      <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
      <span class="text-sm text-slate-500">正在加载日报...</span>
    </section>
    <section v-else-if="loadError" class="flex flex-col items-center gap-3 rounded-xl border border-rose-200 bg-white px-6 py-16 shadow-sm">
      <div class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
        <ExclamationTriangleIcon class="h-6 w-6 text-rose-500" />
      </div>
      <p class="text-sm text-slate-600">日报加载失败，请稍后重试。</p>
      <button type="button" class="btn-secondary mt-1" @click="loadReport(reportDate)">重试</button>
    </section>
    <section v-else-if="!report" class="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white px-6 py-16 shadow-sm">
      <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
        <DocumentTextIcon class="h-6 w-6 text-slate-400" />
      </div>
      <p class="text-sm text-slate-600">当前日期没有可用日报。</p>
      <button type="button" class="btn-secondary mt-1" @click="loadReport(reportDate)">重试</button>
    </section>
    <section v-else-if="isIneligible" class="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 px-5 py-6 shadow-sm sm:px-6">
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
          <ShieldCheckIcon class="h-5 w-5 text-violet-600" />
        </div>
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <ReportStatusBadge :status="report.eligibilityStatus" />
            <h2 class="text-base font-semibold text-slate-900">当前日报无需填写</h2>
          </div>
        </div>
      </div>
      <p class="mt-3 break-words text-sm text-slate-600">{{ report.exemptionReason || report.statusConfirmationReason || '暂无补充说明。' }}</p>
      <p v-if="eligibilitySource" class="mt-2 text-xs text-slate-500">来源：{{ eligibilitySource }}</p>
    </section>
    <section v-else class="pb-28">
      <div class="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div class="flex flex-col gap-3 border-b border-slate-200 px-4 pt-2 sm:px-6">
          <ReportStageTabs v-model="activePhase" />
          <div v-if="isFutureReportDate" class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            未到日期不能填写、保存或提交日报内容。
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2 pb-3">
            <p class="text-sm text-slate-500">{{ activePhase === 'summary' ? summaryDescription : '填写今天的工作安排。' }}</p>
            <div class="flex flex-wrap gap-2">
              <button type="button" class="btn-secondary inline-flex items-center gap-1.5" title="提醒设置" aria-label="提醒设置" @click="openReminderSetting">
                <BellIcon class="h-4 w-4" />
                <span>提醒设置</span>
              </button>
              <button v-if="report?.id" type="button" class="btn-secondary inline-flex items-center gap-1.5" title="评价与反馈" aria-label="评价与反馈" @click="openFeedbackModal">
                <ChatBubbleLeftRightIcon class="h-4 w-4" />
                <span>{{ feedbackButtonLabel }}</span>
                <span v-if="feedbackBadgeLabel" class="ml-0.5 rounded-full bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium leading-4 text-orange-700 ring-1 ring-orange-100">{{ feedbackBadgeLabel }}</span>
              </button>
              <button v-if="activePhase === 'plan' && canAdjustPlan && !planAdjustmentMode" type="button" class="btn-secondary" :disabled="!canStartPlanAdjustment" @click="beginPlanAdjustment">调整计划</button>
              <p v-if="activePhase === 'plan' && canAdjustPlan && !canStartPlanAdjustment && !planAdjustmentMode" class="text-sm text-amber-700">请先保存或放弃总结草稿</p>
              <p v-else-if="activePhase === 'plan' && report.planStatus === 'SUBMITTED' && report.summaryStatus === 'SUBMITTED'" class="text-sm text-slate-500">总结已提交后，计划调整不可用。</p>
              <button v-if="activePhase === 'summary' && report.summaryStatus === 'SUBMITTED' && !summarySupplementMode" type="button" class="btn-secondary" @click="beginSummarySupplement">补充总结</button>
            </div>
          </div>
        </div>

        <div class="space-y-6 px-4 py-4 sm:px-6">
          <template v-if="activePhase === 'plan'">
            <section v-if="planEditable || displayedCarryoverCandidates.length || planEditOptionsLoading" class="carryover-candidate-panel rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-3 sm:px-4">
              <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h2 class="text-base font-semibold text-slate-900">未完成计划候选</h2>
                    <span v-if="carryoverCandidateTotalCount" class="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
                      已选 {{ selectedCarryoverCandidateCount }} / 共 {{ carryoverCandidateTotalCount }}
                    </span>
                  </div>
                  <p class="mt-0.5 text-sm text-slate-500">默认勾选历史未完成待办，可取消不需要带入的计划。</p>
                </div>
                <button
                  v-if="planEditable"
                  type="button"
                  class="btn-secondary inline-flex shrink-0 items-center justify-center gap-1.5"
                  :disabled="planEditOptionsLoading || selectedCarryoverCandidateCount === 0"
                  @click="addSelectedCarryoverCandidates"
                >
                  <PlusIcon class="h-4 w-4" />{{ planEditOptionsLoading ? '加载中' : '加入当前计划' }}
                </button>
              </div>
              <div v-if="planEditOptionsLoading" class="mt-2 flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm text-slate-500 ring-1 ring-slate-100">
                <div class="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
                正在加载候选计划...
              </div>
              <p v-else-if="!displayedCarryoverCandidates.length" class="mt-2 flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm text-slate-500 ring-1 ring-slate-100">
                <InformationCircleIcon class="h-4 w-4" />暂无可带入的未完成计划。
              </p>
              <div v-else class="mt-3 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
                <label
                  v-for="candidate in displayedCarryoverCandidates"
                  :key="candidate.candidateKey"
                  class="carryover-candidate-row flex cursor-pointer items-start gap-3 px-3 py-2.5 transition-colors duration-150 hover:bg-slate-50"
                  :class="{ 'cursor-default opacity-70': !planEditable }"
                >
                  <input
                    v-model="selectedCarryoverCandidateKeys"
                    class="form-check mt-0.5 h-4 w-4 shrink-0"
                    type="checkbox"
                    :value="candidate.candidateKey"
                    :disabled="!planEditable"
                  >
                  <span class="min-w-0 flex-1">
                    <span class="flex items-start justify-between gap-3">
                      <span class="min-w-0">
                        <span class="block break-words text-sm font-medium leading-5 text-slate-800">{{ candidate.title || candidate.todoSnapshot?.title || '--' }}</span>
                        <span class="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] leading-5">
                          <span v-if="candidate.reportDate" class="rounded bg-slate-100 px-1.5 text-slate-500">来源 {{ candidate.reportDate }}</span>
                          <span v-if="candidate.expectedFinishAt" class="rounded bg-blue-50 px-1.5 text-blue-600">预计 {{ candidate.expectedFinishAt }}</span>
                          <button
                            v-if="hasCarryoverCandidateAnnotations(candidate)"
                            type="button"
                            class="rounded bg-violet-50 px-1.5 text-violet-600 transition-colors hover:bg-violet-100 hover:text-violet-700"
                            title="查看批注"
                            @click.stop.prevent="openCarryoverCandidateAnnotations(candidate)"
                          >
                            批注 {{ candidate.annotationCount }}
                          </button>
                        </span>
                      </span>
                      <span class="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-100">待加入</span>
                    </span>
                    <span v-if="candidate.expectedOutcome" class="mt-1 block break-words text-xs leading-5 text-slate-500">预期：{{ candidate.expectedOutcome }}</span>
                  </span>
                </label>
              </div>
            </section>

            <section>
              <div class="mb-2.5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 class="text-base font-semibold text-slate-900">工作项</h2>
                  <p class="mt-0.5 text-sm text-slate-500">计划只能从待办选择，未完成的待办计划可从上方候选带入。</p>
                </div>
                <div v-if="planEditable" class="flex flex-wrap gap-2">
                  <button type="button" class="btn-secondary inline-flex items-center gap-1.5" @click="showTodoPicker = true"><ClipboardDocumentListIcon class="h-4 w-4" />从待办添加</button>
                </div>
              </div>
              <div v-if="workItems.length === 0" class="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center">
                <ClipboardDocumentListIcon class="h-8 w-8 text-slate-300" />
                <span class="text-sm text-slate-500">暂无工作项，请从待办添加或加入未完成计划候选。</span>
              </div>
              <div v-else class="grid gap-3 md:grid-cols-2">
                <ReportWorkItemEditor
                  v-for="(item, index) in workItems"
                  :key="item.id || item.clientKey"
                  :item="item"
                  :errors="planItemErrors[itemKey(item)]"
                  :annotation-count="workItemAnnotationCount(item)"
                  phase="plan"
                  :readonly="!planEditable"
                  @update:item="updateWorkItem(index, $event)"
                  @remove="removeWorkItem(index)"
                  @open-annotations="openTodoAnnotations(item)"
                />
              </div>
            </section>

            <section class="border-t border-slate-200 pt-5">
              <div class="mb-3 flex items-center gap-2">
                <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
                  <PencilSquareIcon class="h-4 w-4 text-blue-600" />
                </div>
                <h2 class="text-sm font-semibold text-slate-800">计划概要</h2>
              </div>
              <div class="grid gap-3 md:grid-cols-2">
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !planEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <StarIcon class="h-3.5 w-3.5 text-amber-500" />
                    <span class="text-xs font-semibold text-slate-600">今日重点</span>
                  </div>
                  <textarea v-model="report.planFocus" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!planEditable" placeholder="请输入今日重点" @input="markPlanDirty"></textarea>
                </label>
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !planEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <UserGroupIcon class="h-3.5 w-3.5 text-blue-500" />
                    <span class="text-xs font-semibold text-slate-600">需要协同</span>
                  </div>
                  <textarea v-model="report.planCollaborationNeed" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!planEditable" placeholder="请输入需要协同的事项" @input="markPlanDirty"></textarea>
                </label>
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !planEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <InformationCircleIcon class="h-3.5 w-3.5 text-slate-400" />
                    <span class="text-xs font-semibold text-slate-600">补充说明</span>
                  </div>
                  <textarea v-model="report.planSupplement" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!planEditable" placeholder="请输入补充说明" @input="markPlanDirty"></textarea>
                </label>
                <label v-if="planAdjustmentMode" class="group relative flex flex-col overflow-hidden rounded-lg border border-amber-200 bg-amber-50/30 transition-all duration-200 cursor-text focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-100 md:col-span-2">
                  <div class="flex items-center gap-1.5 border-b border-amber-100 px-3 py-2">
                    <PencilSquareIcon class="h-3.5 w-3.5 text-amber-600" />
                    <span class="text-xs font-semibold text-amber-800">调整原因<span class="text-red-500">*</span></span>
                  </div>
                  <textarea v-model="adjustmentReason" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-amber-400 focus:outline-none" rows="2" placeholder="请说明调整原因" @input="markPlanDirty"></textarea>
                </label>
              </div>
            </section>
          </template>

          <template v-else>
            <section>
              <div class="mb-2.5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 class="text-base font-semibold text-slate-900">工作项</h2>
                  <p class="mt-0.5 text-sm text-slate-500">填写计划工作项的执行结果。</p>
                  <p v-if="summaryBlockedReason" class="mt-0.5 text-sm text-amber-700">{{ summaryBlockedReason }}</p>
                </div>
              </div>
              <div v-if="workItems.length === 0" class="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center">
                <ClipboardDocumentListIcon class="h-8 w-8 text-slate-300" />
                <span class="text-sm text-slate-500">暂无工作项，可直接填写总结。</span>
              </div>
              <div v-else class="grid gap-3 md:grid-cols-2">
                <ReportWorkItemEditor
                  v-for="(item, index) in workItems"
                  :key="item.id || item.clientKey"
                  :item="item"
                  :annotation-count="workItemAnnotationCount(item)"
                  phase="summary"
                  :readonly="!summaryEditable"
                  @update:item="updateWorkItem(index, $event)"
                  @remove="removeWorkItem(index)"
                  @open-annotations="openTodoAnnotations(item)"
                />
              </div>
            </section>
            <section class="border-t border-slate-200 pt-5">
              <div class="mb-3 flex items-center gap-2">
                <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50">
                  <ClipboardDocumentCheckIcon class="h-4 w-4 text-emerald-600" />
                </div>
                <h2 class="text-sm font-semibold text-slate-800">总结概要</h2>
              </div>
              <div class="grid gap-3 md:grid-cols-2">
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-emerald-300 focus-within:ring-2 focus-within:ring-emerald-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !summaryEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <CheckBadgeIcon class="h-3.5 w-3.5 text-emerald-500" />
                    <span class="text-xs font-semibold text-slate-600">今日成果</span>
                  </div>
                  <textarea v-model="report.summaryAchievement" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!summaryEditable" placeholder="请输入今日成果" @input="markSummaryDirty"></textarea>
                </label>
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-red-300 focus-within:ring-2 focus-within:ring-red-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !summaryEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <ExclamationTriangleIcon class="h-3.5 w-3.5 text-red-400" />
                    <span class="text-xs font-semibold text-slate-600">阻塞事项</span>
                  </div>
                  <textarea v-model="report.summaryBlocker" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!summaryEditable" placeholder="请输入阻塞事项" @input="markSummaryDirty"></textarea>
                </label>
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !summaryEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <HandRaisedIcon class="h-3.5 w-3.5 text-violet-500" />
                    <span class="text-xs font-semibold text-slate-600">需要协助</span>
                  </div>
                  <textarea v-model="report.summaryAssistanceNeeded" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!summaryEditable" placeholder="请输入需要协助的事项" @input="markSummaryDirty"></textarea>
                </label>
                <label class="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white transition-all duration-200 cursor-text focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100 hover:border-slate-300" :class="{ 'opacity-60 pointer-events-none': !summaryEditable }">
                  <div class="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
                    <InformationCircleIcon class="h-3.5 w-3.5 text-slate-400" />
                    <span class="text-xs font-semibold text-slate-600">补充说明</span>
                  </div>
                  <textarea v-model="report.summarySupplement" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" rows="2" :readonly="!summaryEditable" placeholder="请输入补充说明" @input="markSummaryDirty"></textarea>
                </label>
                <label v-if="summarySupplementMode" class="group relative flex flex-col overflow-hidden rounded-lg border border-amber-200 bg-amber-50/30 transition-all duration-200 cursor-text focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-100 md:col-span-2">
                  <div class="flex items-center gap-1.5 border-b border-amber-100 px-3 py-2">
                    <PencilSquareIcon class="h-3.5 w-3.5 text-amber-600" />
                    <span class="text-xs font-semibold text-amber-800">补充原因<span class="text-red-500">*</span></span>
                  </div>
                  <textarea v-model="supplementReason" class="flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 placeholder:text-amber-400 focus:outline-none" rows="2" placeholder="请说明补充原因" @input="markSummaryDirty"></textarea>
                </label>
              </div>
            </section>
          </template>

          <ReportAttachmentList
            :report-id="report.id"
            :attachments="attachments"
            :readonly="!activeEditable"
            :max-count="report.attachmentMaxCount || 20"
            :max-size-mb="attachmentMaxSizeMb"
            :allowed-media-types="report.attachmentAllowedMediaTypes"
            :allowed-extensions="report.attachmentAllowedExtensions"
            @update:attachments="updateAttachments"
          />

        </div>
      </div>

      <div class="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200/80 bg-white/90 px-4 py-3.5 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur-md sm:px-6 lg:left-64">
        <div class="mx-auto flex max-w-7xl flex-wrap items-center justify-end gap-3">
          <span v-if="activeDraftStatus === 'saving'" class="text-xs text-slate-400">自动保存中...</span>
          <button v-if="activeDraftStatus === 'error'" type="button" class="btn-secondary" :disabled="submitting" @click="retryActiveDraft">重试保存</button>
          <button type="button" class="btn-secondary" :disabled="submitting || !activeEditable || isSubmittedPhaseEdit || (activePhase === 'summary' && !canSaveSummary)" @click="saveActiveDraft">保存草稿</button>
          <button type="button" class="btn-primary" :disabled="submitting || !activeEditable || (activePhase === 'summary' && !canSubmitSummary)" @click="submitActivePhase">
            {{ submitting ? '提交中' : activePhase === 'plan' ? '提交计划' : '提交总结' }}
          </button>
        </div>
      </div>
    </section>

    <TodoPickerDrawer v-model="showTodoPicker" :selected-todo-ids="selectedTodoIds" @confirm="addTodos" />
    <WorkReportReminderSettingModal
      v-model="reminderSettingVisible"
      :setting="reminderSetting"
      :loading="reminderSettingLoading"
      :saving="reminderSettingSaving"
      @submit="submitReminderSetting"
    />

    <Teleport to="body">
      <div v-if="showUnsavedModal" class="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="resolveUnsavedChoice('continue')"></div>
        <section class="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div class="px-5 pb-2 pt-5 text-center">
            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <ExclamationTriangleIcon class="h-6 w-6 text-amber-600" />
            </div>
            <h2 class="mt-3 text-base font-semibold text-slate-900">存在未保存修改</h2>
            <p class="mt-2 text-sm leading-6 text-slate-500">离开前可先保存草稿，也可以放弃本次未保存修改。</p>
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <button type="button" class="btn-secondary" @click="resolveUnsavedChoice('continue')">继续编辑</button>
            <button type="button" class="btn-danger-secondary" @click="resolveUnsavedChoice('discard')">放弃</button>
            <button type="button" class="btn-primary" @click="resolveUnsavedChoice('save')">保存草稿</button>
          </div>
        </section>
      </div>

      <div v-if="showPlanSubmitRecipientsModal" class="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="cancelPlanSubmission"></div>
        <section class="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div class="border-b border-slate-100 px-5 pb-4 pt-5">
            <h2 class="text-base font-semibold text-slate-900">设置报送对象</h2>
            <p class="mt-1 text-sm leading-6 text-slate-500">确认本次计划提交的额外接收人与抄送人，固定接收对象由后端规则自动带入。</p>
          </div>
          <div class="space-y-4 px-5 py-4">
            <div class="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-3">
              <div class="text-xs font-semibold text-slate-500">固定接收</div>
              <div v-if="fixedReportRecipients.length" class="mt-2 flex flex-wrap gap-1.5">
                <span
                  v-for="recipient in fixedReportRecipients"
                  :key="`submit-fixed-${recipient.userId}`"
                  class="inline-flex min-w-0 max-w-full items-center rounded-md bg-white px-2 py-1 text-xs text-slate-600 ring-1 ring-slate-200"
                >
                  <span class="truncate">{{ recipient.userNameSnapshot || recipient.userName || recipient.name || `用户 ${recipient.userId}` }}</span>
                </span>
              </div>
              <p v-else class="mt-2 text-xs text-slate-400">暂无固定接收对象</p>
            </div>
            <div class="grid gap-3 md:grid-cols-2">
              <label class="block min-w-0">
                <span class="form-label">额外接收人</span>
                <UserSelector
                  v-model="planSubmitRecipientUsers"
                  class="mt-1"
                  :multiple="true"
                  :disabled="submitting || !recipientEditable"
                  :exclude-ids="planSubmitRecipientExcludeIds"
                  placeholder="请选择额外接收人"
                  @update:model-value="handlePlanSubmitRecipientUsersChange"
                  @confirm="handlePlanSubmitRecipientUsersConfirm"
                />
              </label>
              <label class="block min-w-0">
                <span class="form-label">抄送人</span>
                <UserSelector
                  v-model="planSubmitCcUsers"
                  class="mt-1"
                  :multiple="true"
                  :disabled="submitting || !recipientEditable"
                  :exclude-ids="planSubmitCcExcludeIds"
                  placeholder="请选择抄送人"
                  @update:model-value="handlePlanSubmitCcUsersChange"
                  @confirm="handlePlanSubmitCcUsersConfirm"
                />
              </label>
            </div>
          </div>
          <div class="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <button type="button" class="btn-secondary" :disabled="submitting" @click="cancelPlanSubmission">取消</button>
            <button type="button" class="btn-primary" :disabled="submitting || !recipientEditable" @click="confirmPlanSubmission">
              {{ submitting ? '提交中' : '确认提交计划' }}
            </button>
          </div>
        </section>
      </div>

      <div v-if="showFeedbackModal" class="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="评价与反馈">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="closeFeedbackModal"></div>
        <section class="relative flex max-h-[86vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <header class="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 pb-4 pt-5">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="text-base font-semibold text-slate-900">评价与反馈</h2>
                <span v-if="feedbackBadgeLabel" class="status-badge bg-orange-50 text-orange-700">{{ feedbackBadgeLabel }}</span>
              </div>
              <p class="mt-1 text-sm leading-6 text-slate-500">查看接收人对这份日报的评价、评论与补充要求。</p>
            </div>
            <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭反馈" aria-label="关闭反馈" @click="closeFeedbackModal">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            <ReportFeedbackSection
              :report="report"
              :embedded="true"
              :can-comment="Boolean(report?.id)"
              :submitting="feedbackSubmitting"
              :focus-comment-id="feedbackFocusCommentId"
              :focus-target="feedbackFocusTarget"
              @comment="submitSelfComment"
            />
          </div>
        </section>
      </div>

      <div v-if="showTodoAnnotationModal" class="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="待办批注">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="closeTodoAnnotationModal"></div>
        <section class="relative flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <header class="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 pb-4 pt-5">
            <div class="min-w-0">
              <h2 class="text-base font-semibold text-slate-900">待办批注</h2>
              <p class="mt-1 truncate text-sm text-slate-500">{{ todoAnnotationWorkItem?.title || todoAnnotationWorkItem?.todoSnapshot?.title || '--' }}</p>
            </div>
            <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭" aria-label="关闭" @click="closeTodoAnnotationModal">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>
          <main class="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div v-if="todoAnnotationLoading" class="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
              <div class="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
              正在加载批注...
            </div>
            <div v-else-if="todoAnnotationError" class="flex flex-col items-center gap-3 py-12 text-center">
              <ExclamationTriangleIcon class="h-8 w-8 text-rose-400" />
              <p class="text-sm text-slate-500">批注加载失败，请重试。</p>
              <button type="button" class="btn-secondary" @click="loadTodoAnnotations">重试</button>
            </div>
            <div v-else-if="!todoAnnotations.length" class="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
              暂无批注
            </div>
            <div v-else class="space-y-3">
              <div v-for="annotation in todoAnnotations" :key="annotation.id || annotation.createTime || annotation.content" class="rounded-lg border border-slate-100 bg-slate-50/70 px-3.5 py-3">
                <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                  <span class="font-medium text-slate-600">{{ annotation.creatorName || annotation.createByName || annotation.authorName || '未知用户' }}</span>
                  <span>{{ annotation.createTime || annotation.createdAt || '' }}</span>
                </div>
                <p class="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{{ annotation.content || annotation.remark || '--' }}</p>
              </div>
            </div>
          </main>
          <footer v-if="canAddTodoAnnotation" class="shrink-0 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <label class="block">
              <span class="form-label">新增批注</span>
              <textarea
                v-model="todoAnnotationContent"
                class="form-textarea form-control-sm mt-1 w-full"
                rows="3"
                maxlength="1000"
                placeholder="请输入批注内容"
              ></textarea>
            </label>
            <div class="mt-3 flex justify-end gap-2">
              <button type="button" class="btn-secondary" :disabled="todoAnnotationSubmitting" @click="closeTodoAnnotationModal">取消</button>
              <button type="button" class="btn-primary" :disabled="todoAnnotationSubmitting || !todoAnnotationContent.trim()" @click="submitTodoAnnotation">
                {{ todoAnnotationSubmitting ? '提交中' : '提交批注' }}
              </button>
            </div>
          </footer>
        </section>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { BellIcon, ChatBubbleLeftRightIcon, CheckBadgeIcon, ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, DocumentTextIcon, ExclamationTriangleIcon, HandRaisedIcon, InformationCircleIcon, PencilSquareIcon, PlusIcon, ShieldCheckIcon, StarIcon, UserGroupIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import ListPageShell from '@/components/ListPageShell.vue'
import {
  getDailyWorkReport,
  addWorkItemTodoAnnotation,
  commentWorkReport,
  getPlanEditOptions,
  getSelfWorkReportReminderSetting,
  listWorkItemTodoAnnotations,
  savePlanDraft,
  saveSelfWorkReportReminderSetting,
  saveSummaryDraft,
  submitPlan,
  submitSummary
} from '@/api/workReport'
import {
  createClientRequestId,
  getReportOptimisticVersion,
  normalizeReportTodoSnapshot,
  normalizeTodoStatusCode
} from './workReportUtils'
import { useWorkReportDraft } from './composables/useWorkReportDraft'
import {
  canAdjustPlan as canAdjustPlanForReport,
  canEditSummary as canEditSummaryForState,
  canSaveSummary as canSaveSummaryForState,
  canStartPlanAdjustment as canStartPlanAdjustmentForState,
  canSubmitSummary as canSubmitSummaryForState,
  createWorkReportSubmittedEditStateMachine
} from './composables/workReportSubmittedEditStateMachine.mjs'
import UserSelector from '@/components/UserSelector/index.vue'
import ReportAttachmentList from './components/ReportAttachmentList.vue'
import ReportFeedbackSection from './components/ReportFeedbackSection.vue'
import ReportHeader from './components/ReportHeader.vue'
import WorkReportReminderSettingModal from './components/WorkReportReminderSettingModal.vue'
import ReportStageTabs from './components/ReportStageTabs.vue'
import ReportStatusBadge from './components/ReportStatusBadge.vue'
import ReportWorkItemEditor from './components/ReportWorkItemEditor.vue'
import TodoPickerDrawer from './components/TodoPickerDrawer.vue'

const route = useRoute()
const report = ref(null)
const reportDate = ref(normalizeReportDateQuery(route.query.reportDate) || localDate())
const activePhase = ref(normalizeReportPhaseQuery(route.query.phase))
const loading = ref(false)
const loadError = ref(false)
const submitting = ref(false)
const feedbackSubmitting = ref(false)
const reminderSettingVisible = ref(false)
const reminderSettingLoading = ref(false)
const reminderSettingSaving = ref(false)
const reminderSetting = ref({})
const showTodoPicker = ref(false)
const showFeedbackModal = ref(false)
const feedbackFocusCommentId = ref(normalizeRouteId(route.query.commentId))
const feedbackFocusTarget = ref(isTruthyRouteFlag(route.query.evaluation)
  ? 'evaluation'
  : feedbackFocusCommentId.value ? 'comments' : '')
const attachments = ref([])
const planSubmitRecipientUsers = ref([])
const planSubmitCcUsers = ref([])
const adjustmentReason = ref('')
const supplementReason = ref('')
const planAdjustmentMode = ref(false)
const summarySupplementMode = ref(false)
const planSubmittedEditDirty = ref(false)
const summarySubmittedEditDirty = ref(false)
const planItemErrors = ref({})
const planEditOptionsLoading = ref(false)
const carryoverCandidates = ref([])
const selectedCarryoverCandidateKeys = ref([])
const workItemAnnotationCounts = ref({})
const showTodoAnnotationModal = ref(false)
const todoAnnotationLoading = ref(false)
const todoAnnotationSubmitting = ref(false)
const todoAnnotationError = ref(false)
const todoAnnotationWorkItem = ref(null)
const todoAnnotations = ref([])
const todoAnnotationContent = ref('')
const showUnsavedModal = ref(false)
const showPlanSubmitRecipientsModal = ref(false)
let unsavedChoiceResolver = null
let loadRequestToken = 0

const planDraft = useWorkReportDraft({
  report,
  phase: 'plan',
  save: (payload) => savePlanDraft(report.value.id, payload),
  onConflict: () => ElMessage.error('计划草稿存在版本冲突，请重新加载后再编辑')
})
const summaryDraft = useWorkReportDraft({
  report,
  phase: 'summary',
  save: (payload) => saveSummaryDraft(report.value.id, payload),
  onConflict: () => ElMessage.error('总结草稿存在版本冲突，请重新加载后再编辑')
})
const submittedEditState = createWorkReportSubmittedEditStateMachine()

const workItems = computed(() => report.value?.workItems || [])
const fixedReportRecipients = computed(() => (report.value?.recipients || []).filter((recipient) => !isEditableReportRecipient(recipient)))
const fixedRecipientUserIds = computed(() => selectorUserIds(fixedReportRecipients.value))
const planSubmitRecipientExcludeIds = computed(() => [...fixedRecipientUserIds.value, ...selectorUserIds(planSubmitCcUsers.value)])
const planSubmitCcExcludeIds = computed(() => [...fixedRecipientUserIds.value, ...selectorUserIds(planSubmitRecipientUsers.value)])
const selectedTodoIds = computed(() => workItems.value.map((item) => item.todoId).filter(Boolean))
const existingCarryoverSourceIds = computed(() => new Set(workItems.value.map((item) => item.carryoverFromWorkItemId).filter(Boolean).map(String)))
const displayedCarryoverCandidates = computed(() => carryoverCandidates.value.filter((candidate) => !isCarryoverCandidateAdded(candidate)))
const selectedCarryoverCandidates = computed(() => {
  const selectedKeys = new Set(selectedCarryoverCandidateKeys.value.map(String))
  return displayedCarryoverCandidates.value.filter((candidate) => selectedKeys.has(candidate.candidateKey))
})
const carryoverCandidateTotalCount = computed(() => displayedCarryoverCandidates.value.length)
const selectedCarryoverCandidateCount = computed(() => selectedCarryoverCandidates.value.length)
const hasReportablePlanItem = computed(() => workItems.value.some((item) => isReportablePlanItem(item)))
const canAddTodoAnnotation = computed(() => Boolean(!isIneligible.value
  && !isFutureReportDate.value
  && report.value?.id
  && todoAnnotationWorkItem.value?.id
  && todoAnnotationWorkItem.value?.todoId
  && todoAnnotationWritable(todoAnnotationWorkItem.value)))
const isIneligible = computed(() => ['AUTO_EXEMPT', 'MANUAL_EXEMPT', 'PENDING_STATUS_CONFIRMATION'].includes(report.value?.eligibilityStatus))
const eligibilitySource = computed(() => report.value?.eligibilitySource || report.value?.exemptionSource || '')
const isFutureReportDate = computed(() => isReportDateAfterToday(report.value?.reportDate || reportDate.value))
const canAdjustPlan = computed(() => canAdjustPlanForReport(report.value))
const canStartPlanAdjustment = computed(() => canStartPlanAdjustmentForState({
  summaryHasUnsavedChanges: summaryDraft.hasUnsavedChanges.value,
  summarySaveStatus: summaryDraft.saveStatus.value
}))
const recipientEditable = computed(() => !isIneligible.value && !isFutureReportDate.value && Boolean(report.value?.id))
const planEditable = computed(() => !isIneligible.value && !isFutureReportDate.value && (report.value?.planStatus !== 'SUBMITTED' || (canAdjustPlan.value && planAdjustmentMode.value)))
const planSubmitted = computed(() => report.value?.planStatus === 'SUBMITTED')
const summaryNeedsSupplement = computed(() => reportSupplementRequested(report.value))
const canEditSummary = computed(() => canEditSummaryForState({
  planStatus: report.value?.planStatus,
  planSubmittedEditDirty: planSubmittedEditDirty.value
}))
const canSaveSummary = computed(() => canSaveSummaryForState({
  planStatus: report.value?.planStatus,
  planSubmittedEditDirty: planSubmittedEditDirty.value
}))
const summaryEditable = computed(() => canEditSummary.value && !isIneligible.value && !isFutureReportDate.value && (report.value?.summaryStatus !== 'SUBMITTED' || summarySupplementMode.value || summaryNeedsSupplement.value))
const isSubmittedPlanEdit = computed(() => planAdjustmentMode.value && canAdjustPlan.value)
const isSubmittedSummaryEdit = computed(() => summarySupplementMode.value && report.value?.summaryStatus === 'SUBMITTED')
const isSubmittedPhaseEdit = computed(() => activePhase.value === 'plan' ? isSubmittedPlanEdit.value : isSubmittedSummaryEdit.value)
const activeEditable = computed(() => activePhase.value === 'plan' ? planEditable.value : summaryEditable.value)
const activeDraft = computed(() => activePhase.value === 'plan' ? planDraft : summaryDraft)
const attachmentMaxSizeMb = computed(() => {
  const bytes = Number(report.value?.attachmentMaxSizeBytes)
  return Number.isFinite(bytes) && bytes > 0 ? Math.ceil(bytes / 1024 / 1024) : 20
})
const canSubmitSummary = computed(() => canSubmitSummaryForState({
  planStatus: report.value?.planStatus,
  planSubmittedEditDirty: planSubmittedEditDirty.value
}))
const summaryBlockedReason = computed(() => {
  if (isIneligible.value) return ''
  if (!planSubmitted.value) return '请先提交计划后再填写总结'
  if (!canEditSummary.value) return '请先提交或放弃计划调整'
  return ''
})
const summaryDescription = computed(() => summaryBlockedReason.value || '填写今天的工作结果。')
const hasSubmittedEditChanges = computed(() => planSubmittedEditDirty.value || summarySubmittedEditDirty.value)
const activeDraftStatus = computed(() => hasSubmittedEditChanges.value ? 'dirty' : activeDraft.value.saveStatus.value)
const hasUnsavedChanges = computed(() => planDraft.hasUnsavedChanges.value
  || summaryDraft.hasUnsavedChanges.value
  || planSubmittedEditDirty.value
  || summarySubmittedEditDirty.value)
const feedbackCommentCount = computed(() => Array.isArray(report.value?.comments) ? report.value.comments.length : 0)
const hasCurrentSummaryEvaluation = computed(() => Array.isArray(report.value?.evaluations)
  && report.value.evaluations.some((evaluation) => evaluation?.current && evaluation?.phase === 'SUMMARY'))
const feedbackButtonLabel = computed(() => {
  if (summaryNeedsSupplement.value) return '查看反馈'
  if (hasCurrentSummaryEvaluation.value) return '查看评价'
  if (feedbackCommentCount.value > 0) return '查看评论'
  return '评价与反馈'
})
const feedbackBadgeLabel = computed(() => {
  if (summaryNeedsSupplement.value) return '待补充'
  if (feedbackCommentCount.value > 0) return `${feedbackCommentCount.value}条`
  if (hasCurrentSummaryEvaluation.value) return '已评价'
  return ''
})

watch(activePhase, () => {
  if (activePhase.value === 'summary' && summaryNeedsSupplement.value) summarySupplementMode.value = true
})

watch(
  () => [route.query.reportDate, route.query.phase, route.query.feedback, route.query.commentId, route.query.evaluation],
  async () => {
    const nextPhase = normalizeReportPhaseQuery(route.query.phase)
    if (activePhase.value !== nextPhase) activePhase.value = nextPhase

    const nextDate = normalizeReportDateQuery(route.query.reportDate)
    let dateChanged = false
    if (nextDate && nextDate !== reportDate.value) {
      await changeReportDate(nextDate)
      dateChanged = true
    }
    if (!dateChanged) applyFeedbackRouteQuery()
  }
)

onMounted(() => loadReport(reportDate.value))

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

onBeforeRouteLeave(async () => {
  if (!hasUnsavedChanges.value) return true
  const choice = await requestUnsavedChoice()
  if (choice === 'continue') return false
  if (choice === 'discard') {
    discardAllDrafts()
    return true
  }
  try {
    await flushAllDrafts()
    return true
  } catch {
    return false
  }
})

function handleBeforeUnload(event) {
  if (!hasUnsavedChanges.value) return

  event.preventDefault()
  event.returnValue = ''
}

async function loadReport(date) {
  const requestToken = ++loadRequestToken
  loading.value = true
  loadError.value = false
  try {
    const data = await getDailyWorkReport(date)
    if (requestToken !== loadRequestToken) return
    report.value = data || null
    attachments.value = Array.isArray(data?.attachments) ? data.attachments : []
    adjustmentReason.value = ''
    supplementReason.value = ''
    planAdjustmentMode.value = false
    showPlanSubmitRecipientsModal.value = false
    showFeedbackModal.value = false
    closeTodoAnnotationModal()
    resetPlanEditOptions()
    summarySupplementMode.value = Boolean(reportSupplementRequested(data))
    resetSubmittedEditDirty()
    planItemErrors.value = {}
    if (report.value?.id) await loadPlanEditOptions(report.value.id)
    applyFeedbackRouteQuery()
  } catch {
    if (requestToken !== loadRequestToken) return
    report.value = null
    loadError.value = true
  } finally {
    if (requestToken === loadRequestToken) loading.value = false
  }
}

async function loadPlanEditOptions(reportId) {
  planEditOptionsLoading.value = true
  try {
    const options = await getPlanEditOptions(reportId)
    const candidates = normalizeCollection(options?.carryoverCandidates)
      .map(normalizeCarryoverCandidate)
      .filter(Boolean)
    carryoverCandidates.value = candidates
    selectedCarryoverCandidateKeys.value = candidates
      .filter((candidate) => !isCarryoverCandidateAdded(candidate))
      .map((candidate) => candidate.candidateKey)
    syncWorkItemAnnotationCounts(options?.currentWorkItems)
  } catch {
    ElMessage.warning('未完成计划候选加载失败，可稍后重试')
  } finally {
    planEditOptionsLoading.value = false
  }
}

function resetPlanEditOptions() {
  planEditOptionsLoading.value = false
  carryoverCandidates.value = []
  selectedCarryoverCandidateKeys.value = []
  workItemAnnotationCounts.value = {}
}

function normalizeCollection(value) {
  if (Array.isArray(value)) return value
  return value?.records || value?.list || value?.rows || value?.data || []
}

function normalizeCarryoverCandidate(candidate) {
  if (!candidate || typeof candidate !== 'object') return null
  const todoSnapshot = normalizeReportTodoSnapshot(candidate.todoSnapshot || candidate.currentTodoSnapshot || candidate)
  const todoId = candidate.todoId ?? todoSnapshot?.todoId
  const sourceWorkItemId = candidate.sourceWorkItemId
    ?? candidate.workItemId
    ?? candidate.carryoverFromWorkItemId
    ?? candidate.id
  if (!todoId || !sourceWorkItemId) return null

  const annotations = normalizeCollection(candidate.annotations || candidate.todoAnnotations || candidate.recentAnnotations)
  const annotationCount = Number(candidate.annotationCount ?? candidate.todoAnnotationCount ?? todoSnapshot?.annotationCount ?? annotations.length)
  return {
    ...candidate,
    candidateKey: `carryover-${sourceWorkItemId}`,
    sourceWorkItemId,
    todoId,
    title: candidate.title || candidate.todayGoal || todoSnapshot?.title || '',
    todayGoal: todoDescriptionGoal(todoSnapshot, candidate),
    expectedOutcome: candidate.expectedOutcome || '',
    expectedFinishAt: candidate.expectedFinishAt || todoSnapshot?.dueDate || candidate.dueDate || '',
    reportDate: candidate.reportDate || candidate.sourceReportDate || candidate.planReportDate || '',
    annotations,
    annotationCount: Number.isFinite(annotationCount) ? annotationCount : annotations.length,
    todoSnapshot: todoSnapshot || normalizeReportTodoSnapshot({
      todoId,
      title: candidate.title || candidate.todayGoal,
      description: candidate.description,
      priority: candidate.priority,
      dueDate: candidate.dueDate,
      status: candidate.status,
      completeTime: candidate.completeTime,
      updateTime: candidate.updateTime,
      annotationCount: candidate.annotationCount ?? candidate.todoAnnotationCount ?? annotations.length
    })
  }
}

function hasCarryoverCandidateAnnotations(candidate) {
  const count = Number(candidate?.annotationCount)
  return (Number.isFinite(count) && count > 0)
    || normalizeCollection(candidate?.annotations || candidate?.todoAnnotations).length > 0
}

function syncWorkItemAnnotationCounts(items) {
  const next = { ...workItemAnnotationCounts.value }
  normalizeCollection(items).forEach((item) => {
    const key = item?.id
    if (!key) return
    const annotations = normalizeCollection(item.annotations || item.todoAnnotations)
    const count = Number(item.annotationCount ?? item.todoAnnotationCount ?? item.currentTodoSnapshot?.annotationCount ?? item.todoSnapshot?.annotationCount ?? annotations.length)
    next[String(key)] = Number.isFinite(count) ? count : annotations.length
  })
  workItemAnnotationCounts.value = next
}

function isCarryoverCandidateAdded(candidate) {
  if (!candidate) return true
  if (candidate.sourceWorkItemId && existingCarryoverSourceIds.value.has(String(candidate.sourceWorkItemId))) return true
  if (candidate.todoId && selectedTodoIds.value.map(String).includes(String(candidate.todoId))) return true
  return false
}

async function changeReportDate(nextDate) {
  if (!nextDate || nextDate === reportDate.value) return
  if (hasUnsavedChanges.value) {
    const choice = await requestUnsavedChoice()
    if (choice === 'continue') return
    if (choice === 'discard') discardAllDrafts()
    if (choice === 'save') {
      try {
        await flushAllDrafts()
      } catch {
        return
      }
    }
  }
  reportDate.value = nextDate
  await loadReport(nextDate)
}

function ensureReportDateReached() {
  if (!isFutureReportDate.value) return true
  ElMessage.warning('未到日期不能填写、保存或提交日报内容')
  return false
}

function todoDescriptionGoal(snapshot, todo = {}) {
  return snapshot?.description || todo?.description || snapshot?.title || todo?.title || ''
}

function markPlanDirty() {
  if (!planEditable.value || !report.value) return
  if (isSubmittedPlanEdit.value) {
    markSubmittedEditDirty('plan')
    return
  }
  planDraft.markDirty(buildPlanPayload())
}

function markSummaryDirty() {
  if (!summaryEditable.value || !report.value) return
  if (isSubmittedSummaryEdit.value) {
    markSubmittedEditDirty('summary')
    return
  }
  summaryDraft.markDirty(buildSummaryPayload())
}

function updateWorkItem(index, item) {
  const previous = report.value.workItems[index]
  report.value.workItems.splice(index, 1, item)
  if (activePhase.value === 'plan') {
    clearPlanItemError(previous)
    clearPlanItemError(item)
    markPlanDirty()
  }
  else markSummaryDirty()
}

function removeWorkItem(index) {
  const item = report.value.workItems[index]
  report.value.workItems.splice(index, 1)
  if (activePhase.value === 'plan') {
    clearPlanItemError(item)
    markPlanDirty()
  }
  else markSummaryDirty()
}

function addTodos(todos) {
  const existingIds = new Set(selectedTodoIds.value)
  const todoSnapshots = todos.map((todo) => ({ todo, snapshot: normalizeReportTodoSnapshot(todo) }))
  const skippedUnavailableCount = todoSnapshots
    .filter(({ snapshot }) => snapshot?.todoId && !isPlanTodoReportable(snapshot))
    .length
  const additions = todoSnapshots
    .filter(({ snapshot }) => snapshot?.todoId && isPlanTodoReportable(snapshot) && !existingIds.has(snapshot.todoId))
    .map(({ todo, snapshot }) => ({
      clientKey: createClientRequestId(),
      source: 'TODO',
      todoId: snapshot.todoId,
      title: snapshot.title || todo.title,
      todayGoal: todoDescriptionGoal(snapshot, todo),
      expectedFinishAt: snapshot.dueDate,
      todoAnnotationCount: snapshot.annotationCount,
      todoSnapshot: snapshot
    }))
  if (skippedUnavailableCount > 0) {
    ElMessage.warning('部分待办已完成或取消，已跳过，请刷新后重新选择')
  }
  if (!additions.length) return
  report.value.workItems = [...workItems.value, ...additions]
  markPlanDirty()
}

function addSelectedCarryoverCandidates() {
  const existingTodoIds = new Set(selectedTodoIds.value.map(String))
  const selectedCandidates = selectedCarryoverCandidates.value
    .filter((candidate) => candidate.todoId && candidate.sourceWorkItemId && !existingTodoIds.has(String(candidate.todoId)))
  const skippedUnavailableCount = selectedCandidates
    .filter((candidate) => !isPlanTodoReportable(candidate))
    .length
  const additions = selectedCandidates
    .filter(isPlanTodoReportable)
    .map((candidate) => ({
      clientKey: createClientRequestId(),
      source: 'CARRYOVER',
      todoId: candidate.todoId,
      carryoverFromWorkItemId: candidate.sourceWorkItemId,
      title: candidate.title || candidate.todoSnapshot?.title || '',
      todayGoal: todoDescriptionGoal(candidate.todoSnapshot, candidate),
      expectedOutcome: candidate.expectedOutcome || '',
      expectedFinishAt: candidate.expectedFinishAt || candidate.todoSnapshot?.dueDate || '',
      todoAnnotationCount: candidate.annotationCount ?? candidate.todoSnapshot?.annotationCount,
      todoSnapshot: candidate.todoSnapshot
    }))

  if (skippedUnavailableCount > 0) {
    ElMessage.warning('部分候选待办已完成或取消，已跳过，请刷新后重新选择')
  }
  if (!additions.length) {
    ElMessage.warning('请选择可加入的未完成计划')
    return
  }
  report.value.workItems = [...workItems.value, ...additions]
  selectedCarryoverCandidateKeys.value = selectedCarryoverCandidateKeys.value
    .filter((key) => !additions.some((item) => `carryover-${item.carryoverFromWorkItemId}` === key))
  markPlanDirty()
}

function updateAttachments(nextAttachments) {
  attachments.value = nextAttachments
  if (activePhase.value === 'plan') markPlanDirty()
  else markSummaryDirty()
}

async function saveActiveDraft() {
  if (!ensureReportDateReached()) return
  if (isSubmittedPhaseEdit.value) return
  if (activePhase.value === 'summary' && !canSaveSummary.value) {
    ElMessage.warning(summaryBlockedReason.value || '当前不能保存总结草稿')
    return
  }
  try {
    if (activePhase.value === 'plan') {
      markPlanDirty()
      await planDraft.flush()
    } else {
      markSummaryDirty()
      await summaryDraft.flush()
    }
  } catch {
    ElMessage.error('草稿保存失败，请重试')
  }
}

async function retryActiveDraft() {
  try {
    await activeDraft.value.retry()
  } catch {
    ElMessage.error('草稿保存失败，请重试')
  }
}

async function submitActivePhase() {
  if (!ensureReportDateReached()) return
  if (activePhase.value === 'plan') {
    openPlanSubmitRecipientsModal()
    return
  }
  await submitSummaryPhase()
}

function openPlanSubmitRecipientsModal() {
  if (planAdjustmentMode.value && !canAdjustPlan.value) {
    ElMessage.warning('总结已提交后，计划调整不可用')
    return
  }
  if (!validatePlan()) return
  syncPlanSubmitRecipientsFromReport(report.value)
  showPlanSubmitRecipientsModal.value = true
}

function cancelPlanSubmission() {
  if (submitting.value) return
  showPlanSubmitRecipientsModal.value = false
}

async function confirmPlanSubmission() {
  if (submitting.value) return
  submitting.value = true
  try {
    planDraft.cancelAutoSave()
    const updated = await submitPlan(report.value.id, buildPlanPayload({
      expectedVersion: getReportOptimisticVersion(report.value),
      clientRequestId: createClientRequestId(),
      recipients: buildEditableRecipientsPayload()
    }))
    report.value = updated
    if (report.value?.id) await loadPlanEditOptions(report.value.id)
    planDraft.discard()
    showPlanSubmitRecipientsModal.value = false
    planAdjustmentMode.value = false
    clearSubmittedEditDirty('plan')
    ElMessage.success('计划已提交')
  } catch {
    ElMessage.error('提交失败，请检查网络后重试')
  } finally {
    submitting.value = false
  }
}

async function submitSummaryPhase() {
  if (!canSubmitSummary.value) {
    ElMessage.warning(summaryBlockedReason.value || '当前不能提交总结')
    return
  }
  if (!validateSummary()) return

  submitting.value = true
  try {
    if (!isSubmittedSummaryEdit.value) {
      markSummaryDirty()
      await summaryDraft.flush()
    }
    const updated = await submitSummary(report.value.id, buildSummaryPayload({
      expectedVersion: getReportOptimisticVersion(report.value),
      clientRequestId: createClientRequestId()
    }))
    report.value = updated
    summarySupplementMode.value = false
    clearSubmittedEditDirty('summary')
    ElMessage.success('总结已提交')
  } catch {
    ElMessage.error('提交失败，请检查网络后重试')
  } finally {
    submitting.value = false
  }
}

async function submitSelfComment(comment) {
  if (!report.value?.id || !comment?.content) return
  feedbackSubmitting.value = true
  try {
    await commentWorkReport(report.value.id, {
      parentCommentId: comment.parentCommentId || null,
      content: comment.content,
      clientRequestId: createClientRequestId()
    })
    ElMessage.success('评论已提交')
    await refreshReportFeedback()
  } catch {
    ElMessage.error('评论提交失败，请稍后重试')
  } finally {
    feedbackSubmitting.value = false
  }
}

function workItemAnnotationCount(item) {
  if (!item?.id) return 0
  const cached = workItemAnnotationCounts.value[String(item.id)]
  if (Number.isFinite(Number(cached))) return Number(cached)
  const snapshotCount = Number(item.todoAnnotationCount ?? item.currentTodoSnapshot?.annotationCount ?? item.todoSnapshot?.annotationCount)
  if (Number.isFinite(snapshotCount)) return snapshotCount
  const annotations = normalizeCollection(item.annotations || item.todoAnnotations)
  const count = Number(item.annotationCount ?? item.todoAnnotationCount ?? annotations.length)
  return Number.isFinite(count) ? count : annotations.length
}

function syncWorkItemAnnotationSummary(item, annotations) {
  if (!item?.id) return
  const list = normalizeCollection(annotations)
  const latest = latestTodoAnnotation(list)
  const patch = {
    todoAnnotationCount: list.length,
    todoLatestAnnotationContent: latest?.content || '',
    todoLatestAnnotationTime: latest?.createTime || '',
    todoLatestAnnotationCreatorName: latest?.creatorName || ''
  }
  Object.assign(item, patch)
  const key = itemKey(item)
  const index = workItems.value.findIndex((workItem) => itemKey(workItem) === key)
  if (index >= 0) {
    Object.assign(workItems.value[index], patch)
  }
  workItemAnnotationCounts.value = {
    ...workItemAnnotationCounts.value,
    [String(item.id)]: list.length
  }
}

function latestTodoAnnotation(annotations) {
  return [...normalizeCollection(annotations)]
    .filter((annotation) => annotation?.content)
    .sort(compareTodoAnnotations)
    .at(-1) || null
}

function compareTodoAnnotations(left, right) {
  const timeDiff = annotationTimeValue(left?.createTime) - annotationTimeValue(right?.createTime)
  if (timeDiff !== 0) return timeDiff
  return numericId(left?.id) - numericId(right?.id)
}

function annotationTimeValue(value) {
  const text = String(value || '').trim()
  if (!text) return 0
  const normalized = text.includes('T') ? text : text.replace(' ', 'T')
  const timestamp = Date.parse(normalized)
  return Number.isFinite(timestamp) ? timestamp : 0
}

function numericId(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function todoAnnotationWritable(item) {
  const writable = item?.todoAnnotationWritable
    ?? item?.todoAnnotationCanWrite
    ?? item?.canAddTodoAnnotation
    ?? item?.currentTodoSnapshot?.todoAnnotationWritable
    ?? item?.todoSnapshot?.todoAnnotationWritable
  return Boolean(writable)
}

async function openCarryoverCandidateAnnotations(candidate) {
  const annotations = normalizeCollection(candidate?.annotations || candidate?.todoAnnotations || candidate?.recentAnnotations)
  if (!hasCarryoverCandidateAnnotations(candidate) || !annotations.length) {
    ElMessage.info('暂无可查看的批注')
    return
  }
  todoAnnotationWorkItem.value = {
    id: candidate.sourceWorkItemId,
    todoId: candidate.todoId,
    title: candidate.title || candidate.todoSnapshot?.title || '',
    todoSnapshot: candidate.todoSnapshot,
    annotations,
    todoAnnotationPreloaded: true,
    todoAnnotationWritable: false
  }
  todoAnnotationContent.value = ''
  showTodoAnnotationModal.value = true
  await loadTodoAnnotations()
}

async function openTodoAnnotations(item) {
  if (!item?.id || !item?.todoId) {
    ElMessage.info('保存计划后可查看并新增待办批注')
    return
  }
  todoAnnotationWorkItem.value = item
  todoAnnotationContent.value = ''
  showTodoAnnotationModal.value = true
  await loadTodoAnnotations()
}

async function loadTodoAnnotations() {
  const item = todoAnnotationWorkItem.value
  if (!report.value?.id || !item?.id) return
  if (item.todoAnnotationPreloaded) {
    const annotations = normalizeCollection(item.annotations || item.todoAnnotations)
    todoAnnotations.value = annotations
    syncWorkItemAnnotationSummary(item, annotations)
    return
  }
  todoAnnotationLoading.value = true
  todoAnnotationError.value = false
  try {
    const result = await listWorkItemTodoAnnotations(report.value.id, item.id)
    todoAnnotations.value = normalizeCollection(result)
    syncWorkItemAnnotationSummary(item, todoAnnotations.value)
  } catch {
    todoAnnotationError.value = true
    ElMessage.error('待办批注加载失败，请重试')
  } finally {
    todoAnnotationLoading.value = false
  }
}

function closeTodoAnnotationModal() {
  showTodoAnnotationModal.value = false
  todoAnnotationLoading.value = false
  todoAnnotationSubmitting.value = false
  todoAnnotationError.value = false
  todoAnnotationWorkItem.value = null
  todoAnnotations.value = []
  todoAnnotationContent.value = ''
}

async function submitTodoAnnotation() {
  const content = todoAnnotationContent.value.trim()
  const item = todoAnnotationWorkItem.value
  if (!content || !report.value?.id || !item?.id) return
  todoAnnotationSubmitting.value = true
  try {
    await addWorkItemTodoAnnotation(report.value.id, item.id, { content })
    todoAnnotationContent.value = ''
    ElMessage.success('批注已同步')
    await loadTodoAnnotations()
  } catch {
    ElMessage.error('批注提交失败，请重试')
  } finally {
    todoAnnotationSubmitting.value = false
  }
}

async function openReminderSetting() {
  reminderSettingVisible.value = true
  reminderSettingLoading.value = true
  try {
    reminderSetting.value = await getSelfWorkReportReminderSetting()
  } catch {
    ElMessage.error('提醒设置加载失败，请稍后重试')
  } finally {
    reminderSettingLoading.value = false
  }
}

async function submitReminderSetting(payload) {
  reminderSettingSaving.value = true
  try {
    const saved = await saveSelfWorkReportReminderSetting(payload)
    reminderSetting.value = saved || payload
    reminderSettingVisible.value = false
    ElMessage.success('提醒设置已保存')
  } catch {
    ElMessage.error('提醒设置保存失败，请稍后重试')
  } finally {
    reminderSettingSaving.value = false
  }
}

function openFeedbackModal() {
  if (!report.value?.id) return
  feedbackFocusCommentId.value = ''
  feedbackFocusTarget.value = ''
  showFeedbackModal.value = true
}

function closeFeedbackModal() {
  showFeedbackModal.value = false
}

function applyFeedbackRouteQuery() {
  feedbackFocusCommentId.value = normalizeRouteId(route.query.commentId)
  feedbackFocusTarget.value = isTruthyRouteFlag(route.query.evaluation)
    ? 'evaluation'
    : feedbackFocusCommentId.value ? 'comments' : ''
  if (isTruthyRouteFlag(route.query.feedback) && report.value?.id) {
    showFeedbackModal.value = true
  }
}

async function refreshReportFeedback() {
  if (!report.value?.id) return
  const updated = await getDailyWorkReport(reportDate.value)
  if (!report.value || updated?.id !== report.value.id) return
  report.value.comments = Array.isArray(updated?.comments) ? updated.comments : []
  report.value.evaluations = Array.isArray(updated?.evaluations) ? updated.evaluations : []
  report.value.statusSummary = updated?.statusSummary || report.value.statusSummary
}

function validatePlan() {
  const itemErrors = {}
  workItems.value.forEach((item) => {
    const errors = {}
    const planItemError = planWorkItemError(item)
    if (planItemError) errors.todayGoal = planItemError
    if (Object.keys(errors).length) itemErrors[itemKey(item)] = errors
  })
  planItemErrors.value = itemErrors
  if (Object.keys(itemErrors).length) {
    ElMessage.warning('计划只能从待办或未完成待办计划中选择')
    return false
  }
  if (!hasReportablePlanItem.value) {
    ElMessage.warning('请至少选择一个待办作为今日计划')
    return false
  }
  if (report.value.planStatus === 'SUBMITTED' && !adjustmentReason.value.trim()) {
    ElMessage.warning('已提交计划调整时必须填写调整原因')
    return false
  }
  return true
}

function validateSummary() {
  const invalidResult = workItems.value.find((item) => requiresSummaryResult(item) && !item.result)
  if (invalidResult) {
    ElMessage.warning('计划外工作必须填写执行结果')
    return false
  }
  if (summarySupplementMode.value && !supplementReason.value.trim()) {
    ElMessage.warning('补充总结时必须填写补充原因')
    return false
  }
  return true
}

function requiresSummaryResult(item) {
  return item.source === 'UNPLANNED'
    || (item.source === 'CARRYOVER' && !item.todoId)
}

function isReportablePlanItem(item) {
  return Boolean(item?.todoId || item.source === 'TODO')
}

function isValidPlanWorkItem(item) {
  if (!isPlanWorkItemStructurallyValid(item)) return false
  if (item.id) return true
  return isPlanTodoReportable(item)
}

function planWorkItemError(item) {
  if (!isPlanWorkItemStructurallyValid(item)) return '计划项必须来自可汇报待办'
  if (!item.id && !isPlanTodoReportable(item)) return '新增待办已完成或取消，请刷新后重新选择'
  return ''
}

function isPlanWorkItemStructurallyValid(item) {
  if (item.source === 'TODO') return Boolean(item.todoId)
  if (item.source === 'CARRYOVER') return Boolean(item.todoId && item.carryoverFromWorkItemId)
  return false
}

function isPlanTodoReportable(item) {
  const status = item?.currentTodoSnapshot?.status ?? item?.todoSnapshot?.status ?? item?.status
  const code = normalizeTodoStatusCode(status)
  return code === null || code === 0 || code === 2 || code === 4
}

function handlePlanSubmitRecipientUsersConfirm(users) {
  handlePlanSubmitRecipientUsersChange(selectorUserIds(users))
}

function handlePlanSubmitRecipientUsersChange(value) {
  planSubmitRecipientUsers.value = selectorUserIds(value)
}

function handlePlanSubmitCcUsersConfirm(users) {
  handlePlanSubmitCcUsersChange(selectorUserIds(users))
}

function handlePlanSubmitCcUsersChange(value) {
  planSubmitCcUsers.value = selectorUserIds(value)
}

function syncPlanSubmitRecipientsFromReport(sourceReport) {
  const recipients = Array.isArray(sourceReport?.recipients) ? sourceReport.recipients : []
  planSubmitRecipientUsers.value = recipients
    .filter((recipient) => isEditableReportRecipient(recipient) && recipient.role === 'RECIPIENT')
    .map(selectorUserId)
    .filter(Boolean)
  planSubmitCcUsers.value = recipients
    .filter((recipient) => isEditableReportRecipient(recipient) && recipient.role === 'CC')
    .map(selectorUserId)
    .filter(Boolean)
}

function buildEditableRecipientsPayload(recipientUserIds = planSubmitRecipientUsers.value, ccUserIds = planSubmitCcUsers.value) {
  const fixedIds = new Set(fixedRecipientUserIds.value.map(String))
  const usedIds = new Set(fixedIds)
  return [
    ...recipientPayloadFromUserIds(recipientUserIds, 'RECIPIENT', usedIds),
    ...recipientPayloadFromUserIds(ccUserIds, 'CC', usedIds)
  ]
}

function recipientPayloadFromUserIds(userIds, role, usedIds) {
  const payload = []
  for (const id of selectorUserIds(userIds)) {
    const key = String(id)
    if (!id || usedIds.has(key)) continue
    usedIds.add(key)
    payload.push({
      userId: Number(id),
      role,
      active: true
    })
  }
  return payload
}

function isEditableReportRecipient(recipient) {
  return recipient?.role === 'RECIPIENT' || recipient?.role === 'CC'
}

function selectorUserIds(value) {
  const values = Array.isArray(value) ? value : (value ? [value] : [])
  const usedIds = new Set()
  const result = []
  for (const item of values) {
    const id = selectorUserId(item)
    if (!id || usedIds.has(String(id))) continue
    usedIds.add(String(id))
    result.push(id)
  }
  return result
}

function selectorUserId(value) {
  if (value && typeof value === 'object') return value.id ?? value.userId
  return value
}

function buildPlanPayload(extra = {}) {
  return {
    reportId: report.value.id,
    workItems: serializeWorkItems({ forPlan: true }),
    focus: report.value.planFocus || '',
    collaborationNeed: report.value.planCollaborationNeed || '',
    supplement: report.value.planSupplement || '',
    adjustmentReason: adjustmentReason.value || '',
    attachmentObjectKeys: attachmentObjectKeys(),
    ...extra
  }
}

function buildSummaryPayload(extra = {}) {
  return {
    reportId: report.value.id,
    workItems: serializeWorkItems(),
    achievement: report.value.summaryAchievement || '',
    blocker: report.value.summaryBlocker || '',
    assistanceNeeded: report.value.summaryAssistanceNeeded || '',
    supplement: report.value.summarySupplement || '',
    supplementReason: supplementReason.value || '',
    attachmentObjectKeys: attachmentObjectKeys(),
    ...extra
  }
}

function serializeWorkItems({ forPlan = false } = {}) {
  const items = forPlan ? workItems.value.filter(isValidPlanWorkItem) : workItems.value
  return items.map((item) => normalizeWorkItemForPayload(item, { forPlan }))
}

function normalizeWorkItemForPayload(item, { forPlan = false } = {}) {
  const payload = { ...item }
  delete payload.clientKey
  delete payload.progressPercent
  delete payload.blocker
  delete payload.expectedOutcome
  delete payload.actualOutcome
  delete payload.annotations
  delete payload.todoAnnotations
  delete payload.todoLatestAnnotationContent
  delete payload.todoLatestAnnotationTime
  delete payload.todoLatestAnnotationCreatorName
  delete payload.latestTodoAnnotationContent
  delete payload.latestTodoAnnotationTime
  delete payload.latestTodoAnnotationCreatorName

  const todoSnapshot = normalizeReportTodoSnapshot(payload.todoSnapshot)
  if (todoSnapshot) payload.todoSnapshot = todoSnapshot

  const currentTodoSnapshot = normalizeReportTodoSnapshot(payload.currentTodoSnapshot)
  if (currentTodoSnapshot) payload.currentTodoSnapshot = currentTodoSnapshot

  if (!forPlan) {
    payload.result = normalizeSummaryWorkItemResult(payload.result)
  }

  return payload
}

function defaultSummaryWorkItemResult() {
  return 'IN_PROGRESS'
}

function normalizeSummaryWorkItemResult(result) {
  return result === 'COMPLETED' ? 'COMPLETED' : defaultSummaryWorkItemResult()
}

function attachmentObjectKeys() {
  return attachments.value.map((attachment) => attachment.objectKey).filter(Boolean)
}

async function flushAllDrafts() {
  if (planSubmittedEditDirty.value || summarySubmittedEditDirty.value) {
    ElMessage.warning('已提交日报的调整或补充请直接提交，不能保存草稿')
    throw new Error('submitted work report edit cannot be saved as a draft')
  }
  await planDraft.flush()
  await summaryDraft.flush()
}

function discardAllDrafts() {
  planDraft.discard()
  summaryDraft.discard()
  discardSubmittedEdit('plan')
  discardSubmittedEdit('summary')
}

function beginPlanAdjustment() {
  if (!canAdjustPlan.value) {
    ElMessage.warning('总结已提交后，计划调整不可用')
    return
  }
  if (!canStartPlanAdjustment.value) {
    ElMessage.warning('请先保存或放弃总结草稿')
    return
  }
  planDraft.discard()
  clearSubmittedEditDirty('plan')
  planAdjustmentMode.value = true
}

function beginSummarySupplement() {
  summaryDraft.discard()
  clearSubmittedEditDirty('summary')
  summarySupplementMode.value = true
}

function markSubmittedEditDirty(phase) {
  submittedEditState.markDirty(phase)
  syncSubmittedEditDirty()
}

function clearSubmittedEditDirty(phase) {
  submittedEditState.submitSucceeded(phase)
  syncSubmittedEditDirty()
}

function discardSubmittedEdit(phase) {
  submittedEditState.discard(phase)
  syncSubmittedEditDirty()
}

function resetSubmittedEditDirty() {
  submittedEditState.reset()
  syncSubmittedEditDirty()
}

function syncSubmittedEditDirty() {
  const state = submittedEditState.getState()
  planSubmittedEditDirty.value = state.planSubmittedEditDirty
  summarySubmittedEditDirty.value = state.summarySubmittedEditDirty
}

function itemKey(item) {
  return String(item?.id ?? item?.clientKey ?? item?.todoId ?? '')
}

function clearPlanItemError(item) {
  const key = itemKey(item)
  if (!key || !planItemErrors.value[key]) return
  const nextErrors = { ...planItemErrors.value }
  delete nextErrors[key]
  planItemErrors.value = nextErrors
}

function requestUnsavedChoice() {
  showUnsavedModal.value = true
  return new Promise((resolve) => {
    unsavedChoiceResolver = resolve
  })
}

function resolveUnsavedChoice(choice) {
  showUnsavedModal.value = false
  unsavedChoiceResolver?.(choice)
  unsavedChoiceResolver = null
}

function normalizeReportPhaseQuery(value) {
  const text = Array.isArray(value) ? value[0] : value
  return text === 'summary' ? 'summary' : 'plan'
}

function normalizeReportDateQuery(value) {
  const text = Array.isArray(value) ? value[0] : value
  return /^\d{4}-\d{2}-\d{2}$/.test(String(text || '')) ? text : ''
}

function normalizeRouteId(value) {
  const text = Array.isArray(value) ? value[0] : value
  return text === null || text === undefined || text === '' ? '' : String(text)
}

function isTruthyRouteFlag(value) {
  const text = Array.isArray(value) ? value[0] : value
  return text === true || text === '1' || text === 'true'
}

function reportSupplementRequested(value) {
  return Boolean(value?.statusSummary?.supplementRequested
    || value?.supplementRequested
    || value?.summaryStatus === 'PENDING_SUPPLEMENT')
}

function isReportDateAfterToday(value) {
  const date = normalizeReportDateQuery(value)
  return Boolean(date && date > localDate())
}

function localDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
</script>
